<template>
  <div>
    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-card no-body class="mb-1">
          <template #header>
            <b-skeleton width="50%" />
          </template>
          <b-card-body><b-skeleton width="80%" /></b-card-body>
          <b-list-group flush>
            <b-list-group-item v-for="i of [1,2]" :key="i">
              <b-skeleton width="70%" />
              <b-skeleton width="50%" height="14px" />
            </b-list-group-item>
          </b-list-group>
        </b-card>
        <b-card class="mb-1">
          <template #header>
            <b-skeleton width="50%" />
          </template>
          <b-skeleton width="80%" />
          <b-skeleton width="30%" />
        </b-card>
      </template>
      <b-card no-body v-for="issue in issues" :key="issue.title" class="mb-1">
        <template v-if="issue.ignored">
          <b-card-header>
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <span v-html="issue.title" class="text-muted" />
              </div>
              <div class="text-small">
                <b-link @click="toggleIgnore(issue)" class="text-muted"
                  >Un-mute</b-link
                >
              </div>
            </div>
          </b-card-header>
        </template>
        <template v-else>
          <b-card-header>
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <!-- <b-badge pill variant="danger" class="mr-1 align-baseline">{{issue.links.length}}</b-badge> -->
                <strong><span v-html="issue.title" /></strong>
                <b-icon-info-circle
                  v-if="issue.tooltip && issue.tooltip()"
                  v-b-tooltip="issue.tooltip()"
                  class="ml-1 text-small"
                />
              </div>
              <div class="text-small" v-if="issue.ignorable">
                <b-link @click="toggleIgnore(issue)" class="text-muted"
                  >Mute</b-link
                >
              </div>
              <div
                class="text-small"
                v-else-if="issue.id == 'link_opportunities'"
              >
                <b-link @click="$refs.csv[0].download()" class="text-muted">
                  <Csv
                    name="link_opportunities"
                    :data="link_opportunities"
                    :site="{ url: page.url }"
                    text="Export CSV"
                    ref="csv"
                /></b-link>
                <b-link
                  v-if="hasFeature('links')"
                  @click="toggleNavLinks()"
                  class="ml-2 text-muted"
                  >{{ !ignore_nav ? 'Ignore' : 'Un-Ignore' }} Nav Links</b-link
                >
              </div>
            </div>
          </b-card-header>
          <b-card-body>
            {{ issue.description }}
            <span v-if="issue.links && issue.links.length">
              <template v-if="issue.id != 'no_text' && !issue.id.startsWith('placeholder')">
                {{ issue.links.length > 2 ? 'Sample' : '' }} Anchor Texts:
                <span v-for="(link, i) of issue.links" :key="link.id">
                  <code class="link_text">{{ link.text }}</code>
                  {{ i != issue.links.length - 1 ? ', ' : '' }}
                </span>
              </template>
              <b-link
                v-if="!issue.is_expanded"
                v-b-toggle="`accordion-${issue.id}`"
                class="ml-1 text-nowrap"
                >Details
                <b-icon-chevron-down class="when-closed" /><b-icon-chevron-up
                  class="when-open"
              /></b-link>
            </span>
          </b-card-body>
          <b-collapse
            v-if="issue.links && issue.links.length"
            :visible="issue.is_expanded"
            :id="`accordion-${issue.id}`"
            :accordion="issue.is_expanded ? null : 'issues-accordion'"
            role="tabpanel"
          >
            <b-list-group flush>
              <b-list-group-item v-for="link of issue.links" :key="link.id">
                <div class="link_text">
                  <span v-if="link.text">“{{ link.text }}”</span>
                  <span v-else-if="link.page_links && link.page_links[0].is_image">(Image with no alt text)</span>
                  <span v-else-if="!issue.id.startsWith('placeholder')">(No anchor text)</span>
                </div>
                <template v-if="link.page_links">
                  <a
                    v-for="page_link of link.page_links"
                    :key="page_link.id"
                    :href="page_link.page.url"
                    :title="page_link.page.url"
                    target="_blank"
                    class="text-break text-muted text-small"
                  >
                    <span class="page_url mr-1">{{
                      page_link.page.url | truncate(80)
                    }}</span>
                    <b-icon-box-arrow-up-right class="mr-2" />
                    <b-icon-image v-if="page_link.is_image" title="Image" class="mr-2" />
                  </a>
                  <small
                    v-if="link.total > link.page_links.length"
                    class="text-muted"
                  >
                    ... {{ link.total - link.page_links.length }} more
                  </small>
                </template>
                <span
                  v-else-if="link.href"
                  class="text-break text-muted text-small page_url mr-1"
                  :title="link.href"
                  >{{ link.href | truncate(80) }}</span
                >
              </b-list-group-item>
            </b-list-group>
          </b-collapse>
          <template v-else-if="issue.opportunities">
            <b-list-group flush>
              <b-list-group-item
                v-for="(opportunity, i) of issue.opportunities.slice(0, 5)"
                :key="opportunity.id"
              >
                <div
                  v-if="i == 0 && issue.id == 'link_opportunties'"
                  class="d-flex justify-content-between align-items-center mb-3"
                >
                  <div class="text-small text-muted">
                    Pages missing links to this URL
                  </div>
                  <div class="text-small text-muted">
                    Relevancy
                    <b-icon-info-circle
                      v-b-tooltip.hover="
                        'The frequency of this page\'s top keywords in the article body. To remove a keyword, hit the \'Mute\' icon in the sidebar.'
                      "
                    />
                  </div>
                </div>
                <div class="d-flex justify-content-between align-items-center">
                  <div class="text-small">
                    <div>
                      <span
                        v-for="(word, i) of titleWords(opportunity)"
                        :key="i"
                        :class="
                          (word.relevancy > 0 ? 'text-decoration-underline success' : '') + ' query_word'
                        "
                      >
                        {{ word.word }}
                      </span>
                    </div>
                    <a
                      :href="opportunity.url"
                      :title="opportunity.url"
                      target="_blank"
                      class="text-break text-muted text-small"
                    >
                      <span class="page_url mr-1">{{
                        opportunity.url | truncate(90)
                      }}</span>
                      <b-icon-box-arrow-up-right class="mr-2" />
                    </a>
                  </div>
                  <b-component
                    v-if="'score' in opportunity"
                    :is="`b-icon-${icon(opportunity)}`"
                    class="text-muted"
                    title="Relevancy"
                  />
                </div>
              </b-list-group-item>
            </b-list-group>
          </template>
        </template>
      </b-card>
    </b-skeleton-wrapper>
  </div>
</template>
<style>
.badge .b-icon {
  margin-right: -3px;
}
</style>
<script>
import _ from 'lodash'
import Csv from '@/components/Csv'
import OUTGOING_LINK_OPPORTUNITIES_QUERY from '@/graphql/OutgoingLinkOpportunitiesQuery.gql'
import {
  BIconReception0,
  BIconReception1,
  BIconReception2,
  BIconReception3,
  BIconReception4
} from 'bootstrap-vue'

export default {
  name: 'LinkIssues',
  components: {
    Csv,
    BIconReception0,
    BIconReception1,
    BIconReception2,
    BIconReception3,
    BIconReception4
  },
  props: [
    'account',
    'link_issues',
    'loading',
    'link_opportunities',
    'page',
    'ignore_nav',
    'filter',
    'expanded',
    'isDuda',
  ],

  data: () => {
    return {
      outgoing_link_opportunities: []
    }
  },

  apollo: {
    outgoing_link_opportunities: {
      query: OUTGOING_LINK_OPPORTUNITIES_QUERY,
      variables() {
        return {
          url: this.page.url,
          site_id: this.page.site_id,
        }
      },
      skip() {
        return !this.page.id || !this.isDuda
      },
    },
  },
  computed: {
    issues() {
      const content = {
        low_anchor_diversity: {
          title: 'Increase Anchor Diversity',
          description: `We found very few anchor text variations to this page. Edit or add links to increase your diversity.`,
          ignorable: true
        },
        no_outgoing_internal_links: {
          title: 'Add Outgoing Internal Links',
          description: `We found no links to other pages on your site within the main text of the page. Add internal links to relevant pages.`,
          variant: 'warning',
          icon: 'exclamation-triangle-fill',
          ignorable: false
        },
        broken_links: {
          title: 'Fix Broken Links',
          description: `Fix the broken internal links on this page.`,
          ignorable: true
        },
        no_text: {
          title: 'Fix Missing Anchors',
          description: `You have links with empty anchor text pointing to this page. Add text to make these links descriptive and helpful.`,
          ignorable: true
        },
        is_generic: {
          title: 'Make Anchors Descriptive',
          description: `Edit your generic anchor text links to make them more descriptive.`,
          ignorable: true
        },
        has_no_keywords: {
          title: 'Add Keywords to Anchors',
          description: `We couldn't find any top keywords in your incoming anchor text. Edit these to be more specific.`,
          ignorable: true
        },
        is_nofollow: {
          title: 'Verify nofollow Links',
          description: `Consider removing the nofollow attribute of incoming links, if appropriate.`,
          variant: 'warning',
          icon: 'exclamation-triangle-fill',
          ignorable: false
        },
        is_exact_query_match: {
          title: 'Add Partial Match Anchors',
          description: `All anchors to this page are exact match: they match top search queries exactly. Edit or add links to include partial match anchors and greater diversity.`,
          ignorable: true
        },
        placeholder_link: {
          title: 'Remove placeholder links on this page',
          description: `This page contains placeholder anchor text from the original template. Make sure your anchor text is always descriptive and helpful.`,
          ignorable: true
        },
        placeholder_social: {
          title: 'Replace these default social network links',
          description: `It looks like these social network/share links need to be updated to point to your specific profiles.`,
          ignorable: true
        },
      }

      const issues = this.link_issues.map((i) => ({
        ...i,
        ...content[i.id]
      }))
      if (!issues.length) {
        issues.push({
          ok: {
            title: 'This page has good internal inbound links and anchor text.',
            description: ``,
            ignorable: false,
            variant: 'success',
            icon: 'check-circle-fill'
          }
        })
      }
      const link_quantity = this.link_issues.find((i) =>
        ['no_links', 'few_links'].includes(i.id)
      )
      if (link_quantity) {
        let description = `We found ${
          link_quantity.id == 'no_links' ? 'no' : 'very few'
        } internal links to this URL.`
        if (this.link_opportunities.length) {
          issues.unshift({
            id: 'link_opportunities',
            title: 'Inbound Link Opportunities',
            description: `${description} Add relevant links using descriptive anchor text.`,
            opportunities: this.link_opportunities,
            tooltip: () => ({
              title: `Other pages on your site that contain this page's top keywords, but do not link to this page, sorted by relevancy`
            }),
            ignorable: false
          })
        } else {
          issues.unshift({
            id: 'link_opportunities',
            title: 'Inbound Link Opportunities',
            description: `${description} Consider creating additional relevant content around this subject to link to.`,
            ignorable: false
          })
        }
      } else if (this.link_opportunities.length) {
        issues.unshift({
          id: 'link_opportunities',
          title: 'Inbound Link Opportunities',
          description: `Adding links from these most-relevant pages may help it rank.`,
          variant: 'secondary',
          icon: 'info-circle-fill',
          opportunities: this.link_opportunities,
          tooltip: () => ({
            title: `Other pages on your site that contain this page's top keywords, but do not link to this page, sorted by relevancy`
          }),
          ignorable: false
        })
      } else {
        // @TODO add navigation check
        issues.unshift({
          id: 'link_opportunities',
          title: 'Inbound Link Opportunities',
          // description: `Because this page is in the navigation, we found no good opportunities that weren't already linking.`,
          description: `No inbound link opportunities found for this URL.`,
          tooltip: () => ({
            title: `We didn't find any pages that contain this page's top keywords, but do not link to this page`
          }),
          variant: 'secondary',
          icon: 'info-circle-fill',
          ignorable: false
        })
      }
      if(this.outgoing_link_opportunities.length) {
        let issue = issues.find(e => e.id == 'no_outgoing_internal_links')
        if(!issue) {
          issue = {
            id: 'outgoing_link_opportunities',
            title: 'Outgoing Link Opportunities',
            description: `Adding links to these most-relevant pages may help them rank.`,
            tooltip: () => ({
              title: `This page contains top keywords for other pages, but does not link to them.`
            }),
            ignorable: false
          }
          issues.push(issue)
        }
        issue.opportunities = this.outgoing_link_opportunities
      }
      issues.forEach((i) => {
        const pi = _.get(this.page, 'page_issues', []).find(
          (pi) => pi.name == i.id
        )
        if (pi) {
          i.ignored = pi.ignored
        }
        i.is_expanded = this.expanded == i.id
      })
      return _.orderBy(issues.filter((e) => e.title), 'is_expanded', 'desc')
    }
  },

  watch: {
    filter() {
      this.$emit('update:filter', this.filter)
    }
  },

  methods: {
    hasFeature(feature) {
      return (
        this.account[0].is_admin ||
        this.account[1] ||
        (this.account[0].features || []).includes(feature)
      )
    },
    pathname(url) {
      return new URL(url).pathname
    },
    icon(opportunity) {
      const n = Math.round((opportunity.score || 0) / 25)
      return `reception${n}`
    },
    toggleIgnore(issue) {
      this.$emit('ignore', issue.id, issue.ignored)
      _zyppy.track(`Issue ${issue.ignored ? 'Un-' : ''}Muted`, {
        name: issue.id,
        page_id: this.page.id,
        type: 'links'
      })
    },
    toggleNavLinks() {
      this.$emit('update:ignore_nav', !this.ignore_nav)
      _zyppy.track(`Nav links ${!this.ignore_nav ? 'Ignored' : 'Shown'}`, {
        page_id: this.page.id
      })
    },
    titleWords(opportunity) {
      return opportunity.title.split(/\s+/).map((word) => {
        const lword = word.toLowerCase()
        const { relevancy } =
          (opportunity.words || []).find(
            (e) => e.word.toLowerCase() == lword || lword.startsWith(e.stem)
          ) || {}
        return { word, relevancy }
      })
    }
  }
}
</script>